import { Link, createFileRoute } from "@tanstack/react-router";
import { Compass, Sparkles, TrendingUp } from "lucide-react";
import { motion } from "motion/react";
import { type ReactNode } from "react";

import { CihArticleRows } from "~/components/cih/article-rows";
import { IcfPartnerBox } from "~/components/icf-partner-box";
import { GoalsShowcase } from "~/components/showcase/goals-showcase";
import { SessionTimelineShowcase } from "~/components/showcase/session-timeline-showcase";
import { StoriesCarousel } from "~/components/stories-carousel";
import { SummitPromoCard } from "~/components/summit/summit-promo-card";
import { Button } from "~/components/ui/button";
import { CDNImage } from "~/components/ui/cdn-image";
import { CtaBanner } from "~/components/ui/cta-banner";
import { FeatureShowcase } from "~/components/ui/feature-showcase";
import { Footer } from "~/components/ui/footer";
import { Header } from "~/components/ui/header";
import { Heading } from "~/components/ui/heading";
import { HeroBackground } from "~/components/ui/hero-background";
import { IconFeatureGrid } from "~/components/ui/icon-feature-grid";
import { NamedIcon } from "~/components/ui/named-icon";
import { cn } from "~/lib/cn";
import { getArticlesIndexFn } from "~/lib/functions/cih/articles";
import { getExpertiseCategories } from "~/lib/functions/expertise";
import { useCardTilt } from "~/lib/hooks/use-card-tilt";
import {
  BRAND,
  BRAND_ORGANIZATION_ID,
  BRAND_PEOPLE,
  BRAND_URL,
} from "~/lib/shared/brand";
import {
  createBrandMeta,
  createBrandPersonStructuredData,
  createBrandWebsiteStructuredData,
  createBreadcrumbStructuredData,
} from "~/lib/shared/meta";
import { optionalLoaderData } from "~/lib/shared/optional-loader-data";

import { PrivacySection } from "./-components/privacy-section";

type ExpertiseCategoryOption = Awaited<
  ReturnType<typeof getExpertiseCategories>
>[number];

type CoachStep = {
  readonly title: string;
  readonly description: string;
  readonly visual: ReactNode;
};

const howItWorks = [
  {
    title: "Discover your path",
    description:
      "Our guided intake helps you define what you want to achieve and matches you with coaches who specialize in your goals.",
    badgeNumber: 1,
  },
  {
    title: "Design your vision",
    description:
      "Map out your life vision, set meaningful goals, and share them with your coach so sessions have real direction.",
    badgeNumber: 2,
  },
  {
    title: "Track your progress",
    description:
      "Session notes, action items, and milestone tracking keep you accountable between sessions.",
    badgeNumber: 3,
  },
];

const coachSteps: CoachStep[] = [
  {
    title: "Apply to join the marketplace",
    description:
      "Share your specialties and credentials so we can introduce you to clients who match your expertise.",
    visual: (
      <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-brand-50 text-lg font-semibold text-brand-700">
        1
      </div>
    ),
  },
  {
    title: "Publish a standout profile",
    description:
      "Create a polished page with your offer, testimonials, and availability so clients can book discovery calls.",
    visual: (
      <CDNImage
        className="aspect-[3/2] w-full rounded-xl object-cover"
        src="https://form-data.dreamcoachmatch.com/public/screenshots/profile.png"
        alt="DreamCoachMatch coach profile preview"
        width={320}
        height={200}
      />
    ),
  },
  {
    title: "Meet clients through discovery",
    description:
      "Get matched with motivated clients, host calls, and manage billing from one streamlined dashboard.",
    visual: (
      <CDNImage
        className="aspect-[3/2] w-full rounded-xl object-cover"
        src="https://form-data.dreamcoachmatch.com/public/screenshots/match.png"
        alt="DreamCoachMatch discovery matching preview"
        width={320}
        height={200}
      />
    ),
  },
];

// — Hero choice cards —————————————————————————————————————————————

const CARD_CONTAINER_VARIANTS = {
  hidden: {},
  show: { transition: { staggerChildren: 0.12, delayChildren: 0.1 } },
} as const;

const CARD_ITEM_VARIANTS = {
  hidden: { opacity: 0, y: 28 },
  show: {
    opacity: 1,
    y: 0,
    transition: { type: "spring", stiffness: 340, damping: 26 },
  },
} as const;

type HeroCardProps = {
  readonly to: string;
  readonly params?: Record<string, string>;
  readonly icon: ReactNode;
  readonly title: string;
  readonly subtitle: string;
  readonly gradientClass: string;
  readonly cta?: string;
  /** Optional second destination, kept reachable beside the primary action. */
  readonly secondaryTo?: string;
  readonly secondaryParams?: Record<string, string>;
  readonly secondaryLabel?: string;
};

const HeroCard = ({
  to,
  params,
  icon,
  title,
  subtitle,
  gradientClass,
  cta = "Get started",
  secondaryTo,
  secondaryParams,
  secondaryLabel,
}: HeroCardProps) => {
  const tilt = useCardTilt<HTMLDivElement>();

  return (
    <motion.div variants={CARD_ITEM_VARIANTS} className="flex w-full sm:flex-1">
      <div
        ref={tilt.ref}
        onMouseMove={tilt.onMouseMove}
        onMouseLeave={tilt.onMouseLeave}
        className={cn(
          "rotate-dynamic dcm-shiny-overlay hero-card-reset",
          "group relative flex h-full flex-col rounded-2xl",
          gradientClass,
          "border border-white/20 hover:border-white/30",
          "transition-shadow duration-500 ease-out-expo",
          "shadow-lg shadow-black/10 hover:shadow-xl hover:shadow-black/20",
        )}
      >
        <Link
          to={to}
          params={params}
          className={cn(
            "flex flex-1 flex-col rounded-2xl p-7 sm:p-8",
            "min-h-[210px] sm:min-h-[230px]",
            "transition-shadow duration-300 ease-out-expo",
            "focus-visible:ring-2 focus-visible:ring-white/50 focus-visible:ring-offset-2 focus-visible:ring-offset-purple-900 focus-visible:outline-none",
            "cursor-pointer",
          )}
        >
          <div className="mb-2 flex flex-1 flex-col gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-white/15 backdrop-blur-sm transition-colors duration-300 group-hover:bg-white/25">
              {icon}
            </div>

            <h3 className="font-heading text-xl leading-tight font-medium text-white">
              {title}
            </h3>
          </div>

          <p className="text-sm leading-relaxed text-white/70">{subtitle}</p>

          <span className="flex items-center gap-2 pt-2 text-sm font-medium text-white/85">
            {cta}
            <span
              aria-hidden
              className="inline-block transition-transform duration-200 ease-out-expo group-hover:translate-x-1"
            >
              &rarr;
            </span>
          </span>
        </Link>

        {secondaryTo && secondaryLabel && (
          <Link
            to={secondaryTo}
            params={secondaryParams}
            className={cn(
              "relative z-10 border-t border-white/15 px-7 py-4 sm:px-8",
              "text-sm font-medium text-white/75 underline underline-offset-4",
              "transition-colors duration-200 hover:text-white",
              "focus-visible:ring-2 focus-visible:ring-white/50 focus-visible:outline-none",
            )}
          >
            {secondaryLabel}
          </Link>
        )}
      </div>
    </motion.div>
  );
};

const HeroChoiceCards = () => (
  <motion.div
    className="mt-8 flex w-full max-w-2xl flex-col gap-4 sm:flex-row"
    variants={CARD_CONTAINER_VARIANTS}
    initial="hidden"
    animate="show"
  >
    <HeroCard
      to="/intake"
      icon={<Compass className="h-5 w-5 text-white" />}
      title="I want to make a change"
      subtitle="Not sure what you need? We'll help you figure it out."
      gradientClass="bg-gradient-to-br from-purple-600 via-purple-700 to-purple-800"
      cta="Discover your path"
    />
    <HeroCard
      to="/6-figure-readiness-score"
      icon={<TrendingUp className="h-5 w-5 text-white" />}
      title="I coach professionally"
      subtitle="Take the free 6-Figure Readiness Score in about two minutes and see what to build first on the way to $100K a year."
      gradientClass="bg-gradient-to-br from-violet-600 via-violet-700 to-indigo-700"
      cta="Get your free score"
      secondaryTo="/page/$name"
      secondaryParams={{ name: "coach" }}
      secondaryLabel="How joining as a coach works"
    />
  </motion.div>
);

// — Latest articles ————————————————————————————————————————————————

type HomeArticleRow = Awaited<ReturnType<typeof getArticlesIndexFn>>[number];

/** How many of the newest published CIH articles the homepage surfaces. */
const LATEST_ARTICLES_COUNT = 5;

const HeroSection = () => (
  <div className="relative mx-auto w-full max-w-screen-xl md:mt-4">
    <HeroBackground className="absolute inset-0 h-full max-w-full rounded-2xl" />
    <div className="relative z-10 mx-auto flex max-w-2xl flex-col items-center justify-center px-4 py-12 pt-24 md:py-36 md:pt-36">
      <span className="mb-1 text-center text-sm font-medium tracking-wide text-purple-700 uppercase">
        Vetted coaches. Personalized guidance.
      </span>
      <h1 className="mb-4 flex flex-col items-center justify-center gap-4 text-center font-heading text-4xl font-bold tracking-tight md:text-5xl lg:text-6xl">
        {/* Decorative avatar cluster — purely illustrative, so hidden from
            assistive tech and given empty alt so the H1 reads as plain text. */}
        <span aria-hidden="true" className="flex items-center justify-center">
          <CDNImage
            src="https://form-data.dreamcoachmatch.com/public/avatar-1.jpg"
            alt=""
            width={64}
            height={64}
            className="-animate-delay-14000 shadow-opacity-50 z-0 aspect-square h-12 w-12 animate-breathing rounded-full border-2 border-white shadow transition ease-out-expo hover:z-30 hover:scale-102 hover:rotate-3 hover:animate-none hover:shadow-lg md:h-14 md:w-14 lg:h-16 lg:w-16"
          />
          <CDNImage
            src="https://form-data.dreamcoachmatch.com/public/avatar-2.jpg"
            alt=""
            width={64}
            height={64}
            className="shadow-opacity-50 z-10 -ml-4 aspect-square h-12 w-12 animate-breathing rounded-full border-2 border-white shadow transition ease-out-expo hover:z-30 hover:scale-102 hover:animate-none hover:shadow-lg md:h-14 md:w-14 lg:h-16 lg:w-16"
          />
          <CDNImage
            src="https://form-data.dreamcoachmatch.com/public/avatar-3.jpg"
            alt=""
            width={64}
            height={64}
            className="-animate-delay-7000 shadow-opacity-50 z-20 -ml-4 aspect-square h-12 w-12 animate-breathing rounded-full border-2 border-white shadow transition ease-out-expo hover:z-30 hover:scale-102 hover:animate-none hover:shadow-lg md:h-14 md:w-14 lg:h-16 lg:w-16"
          />
        </span>
        The global home for coaching.
      </h1>

      <p className="text-text-secondary mb-4 text-center sm:text-lg md:mb-8">
        The right guidance changes everything. We help you figure out exactly
        what you need &mdash; whether that&rsquo;s a coach, a group program, or
        a structured process.
      </p>

      <HeroChoiceCards />

      <SummitPromoCard className="mt-12" />

      <div className="mt-12 rounded-2xl bg-white/40 p-6 shadow-lg shadow-brand-800/10 backdrop-blur">
        <IcfPartnerBox />
      </div>
    </div>
  </div>
);

const CategoriesSection = ({
  categories,
}: {
  readonly categories: ExpertiseCategoryOption[];
}) => (
  <section className="mx-auto max-w-screen-xl px-4 py-20">
    <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
      <div className="flex flex-col gap-4">
        <div>
          <p className="mb-1 text-sm font-medium tracking-wide text-purple-700 uppercase">
            Coaches by category
          </p>
          <h2 className="text-text-primary font-heading text-3xl font-medium tracking-tight md:text-4xl">
            Explore expert coaches by category
          </h2>
        </div>
        <p className="text-text-secondary text-lg leading-relaxed">
          Browse our most-requested categories to see coaches who specialize in
          the outcomes you care about.
        </p>
      </div>
      <Link to="/coaches/category" className="shrink-0">
        <Button>View all categories</Button>
      </Link>
    </div>

    <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
      {categories.map((cat) => (
        <Link
          key={cat.id}
          to="/coaches/category/$categorySlug"
          params={{ categorySlug: cat.slug }}
          className="group relative flex flex-col rounded-2xl border border-gray-100 bg-white p-7 shadow-sm transition duration-500 ease-out-expo hover:scale-[1.02] hover:shadow-xl focus:ring-2 focus:ring-brand-400 focus:outline-none"
          aria-label={`View ${cat.listingName || cat.name} coaches`}
        >
          <span className="absolute inset-0" aria-hidden="true"></span>
          <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-xl bg-brand-50 transition-colors group-hover:bg-brand-100">
            <NamedIcon
              name={cat.icon || ""}
              className="h-8 w-8 text-brand-700"
            />
          </div>
          <h3 className="text-text-primary mb-2 font-heading text-xl font-medium tracking-tight">
            {cat.listingName || cat.name}
          </h3>
          {cat.description && (
            <p className="text-text-secondary leading-relaxed">
              {cat.description}
            </p>
          )}
          <div className="mt-3 flex items-center gap-2 font-semibold text-brand-600">
            Browse coaches
            <span aria-hidden="true">→</span>
          </div>
        </Link>
      ))}
    </div>
  </section>
);

const CoachGuideSection = ({ steps }: { readonly steps: CoachStep[] }) => (
  <section className="bg-gradient-to-b from-brand-50/60 via-white to-white py-20">
    <div className="mx-auto max-w-screen-xl px-4">
      <Heading
        align="left"
        preHeadline="How to become a DreamCoachMatch coach"
        headline="Get listed and start meeting clients"
        description="Follow these steps to publish a profile that stands out and connects you with clients who value your expertise."
      />

      <div className="mt-10 grid gap-8 lg:grid-cols-3">
        {steps.map((step) => (
          <div
            key={step.title}
            className="flex h-full flex-col gap-4 rounded-2xl border border-brand-100/70 bg-white p-6 shadow-sm transition duration-500 ease-out-expo hover:-translate-y-1 hover:border-brand-200 hover:shadow-xl"
          >
            <div className="overflow-hidden rounded-xl">{step.visual}</div>
            <h3 className="text-text-primary font-heading text-xl font-medium">
              {step.title}
            </h3>
            <p className="text-text-secondary leading-relaxed">
              {step.description}
            </p>
          </div>
        ))}
      </div>

      <div className="mt-10 flex justify-center">
        <Link to="/page/$name" params={{ name: "coach" }}>
          <Button size="lg" variant="fancy">
            Apply to join the marketplace
          </Button>
        </Link>
      </div>
    </div>
  </section>
);

const PathForkSection = () => (
  <section className="mx-auto max-w-screen-xl px-4 py-16">
    <div className="mx-auto flex max-w-2xl flex-col items-center gap-8">
      <h2 className="text-text-primary text-center font-heading text-2xl font-medium tracking-tight sm:text-3xl">
        What sounds like you?
      </h2>

      <div className="grid w-full gap-4 sm:grid-cols-2">
        <Link
          to="/intake"
          className="group flex flex-col gap-3 rounded-2xl border border-brand-100 bg-white p-6 shadow-sm transition duration-500 ease-out-expo hover:-translate-y-1 hover:border-brand-200 hover:shadow-xl"
        >
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand-50 transition-colors group-hover:bg-brand-100">
            <Compass className="h-5 w-5 text-brand-700" />
          </div>
          <h3 className="text-text-primary font-heading text-lg font-medium">
            I&rsquo;m still figuring things out
          </h3>
          <p className="text-text-secondary text-sm leading-relaxed">
            Take a guided assessment and get a personalized recommendation for
            your next step.
          </p>
          <span className="flex items-center gap-2 text-sm font-medium text-brand-600">
            Take the guided assessment
            <span
              aria-hidden
              className="inline-block transition-transform duration-200 ease-out-expo group-hover:translate-x-1"
            >
              &rarr;
            </span>
          </span>
        </Link>

        <Link
          to="/discovery"
          className="group flex flex-col gap-3 rounded-2xl border border-brand-100 bg-white p-6 shadow-sm transition duration-500 ease-out-expo hover:-translate-y-1 hover:border-brand-200 hover:shadow-xl"
        >
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand-50 transition-colors group-hover:bg-brand-100">
            <Sparkles className="h-5 w-5 text-brand-700" />
          </div>
          <h3 className="text-text-primary font-heading text-lg font-medium">
            I know I want a coach
          </h3>
          <p className="text-text-secondary text-sm leading-relaxed">
            Our AI-powered match quiz pairs you with coaches based on your goals
            and preferences.
          </p>
          <span className="flex items-center gap-2 text-sm font-medium text-brand-600">
            Find your match
            <span
              aria-hidden
              className="inline-block transition-transform duration-200 ease-out-expo group-hover:translate-x-1"
            >
              &rarr;
            </span>
          </span>
        </Link>
      </div>

      <p className="text-text-secondary text-center text-sm">
        Not ready to choose? Take the{" "}
        <Link
          to="/wheel-of-life"
          className="font-medium text-brand-600 transition-colors hover:text-brand-700"
        >
          free Wheel of Life assessment
        </Link>{" "}
        or{" "}
        <Link to="/coaches" className="transition-colors hover:text-brand-600">
          browse all coaches directly &rarr;
        </Link>
      </p>
    </div>
  </section>
);

/**
 * "Latest articles" — the newest published Coaching Intelligence articles
 * (the `/articles` library), reusing the CIH row list so the homepage links
 * into the same content the library serves. The byline is the coach for
 * coach-authored articles, the editorial author for house guides.
 */
const ArticlesSection = ({
  articles,
}: {
  readonly articles: readonly HomeArticleRow[];
}) => {
  if (articles.length === 0) return null;

  return (
    <section>
      <CihArticleRows
        articles={articles.map((row) => ({
          id: row.article.id,
          slug: row.article.slug,
          title: row.article.title,
          description: row.article.description,
          authorName:
            row.coach?.fullName ??
            row.article.authorName ??
            "Dream Coach Match",
          challengeTitle: row.challenge.title,
          challengeSlug: row.challenge.slug,
        }))}
        sectionLabel="Coaching Intelligence"
        heading="Latest articles"
        showChallenge
      />
      {/* Pull the CTA up against the row list's built-in bottom padding. */}
      <div className="mx-auto -mt-6 max-w-7xl px-4 pb-20 md:-mt-10 xl:px-0">
        <Link to="/articles">
          <Button>Browse all articles</Button>
        </Link>
      </div>
    </section>
  );
};

function HomePage() {
  const { categories, articles } = Route.useLoaderData();
  const visibleCategories = categories.slice(0, 6);

  return (
    <>
      <Header />

      <HeroSection />

      <IconFeatureGrid
        tone="muted"
        columns={3}
        preHeadline="Your Journey"
        headline="From clarity to results in three steps"
        items={howItWorks}
      />

      <PathForkSection />

      <FeatureShowcase
        preHeadline="Life Design"
        headline="See the big picture. Track every step."
        description="Define your overall life vision across the categories that matter to you — career, health, relationships, and more. Set specific goals with milestones, and watch your progress unfold over time. Your coach sees exactly what you're working toward, so every session moves you forward."
        visual={<GoalsShowcase />}
        ctaText="Start designing your life"
        ctaHref="/intake"
      />

      <FeatureShowcase
        preHeadline="Coaching Sessions"
        headline="Every session builds on the last."
        description="Keep track of what happened in each session and what comes next. Session notes, action items, and homework live in one place — visible to both you and your coach. No more forgotten follow-ups or lost momentum."
        visual={<SessionTimelineShowcase />}
        ctaText="Find your match"
        ctaHref="/discovery"
        reverse
        className="bg-gradient-to-b from-white to-brand-50/30"
      />

      <PrivacySection />

      <StoriesCarousel />
      <CategoriesSection categories={visibleCategories} />
      <CoachGuideSection steps={coachSteps} />
      <ArticlesSection articles={articles} />

      <div className="mx-auto max-w-screen-xl px-4 pb-20">
        <CtaBanner
          headline="Not sure where to start?"
          description="Our guided assessment takes a few minutes and recommends the right path — no commitment required."
          buttonText="Take the assessment"
          buttonHref="/intake"
        >
          <Link
            to="/coaches"
            className="mt-4 text-sm text-purple-300/70 transition-colors hover:text-purple-200"
          >
            Or browse coaches directly &rarr;
          </Link>
        </CtaBanner>
      </div>

      <Footer />
    </>
  );
}

export const Route = createFileRoute("/")({
  staleTime: 6 * 60 * 60 * 1000, // 6 hours
  loader: async () => {
    const [categories, articles] = await Promise.all([
      getExpertiseCategories(),
      // The index query is publishedAt DESC + LIMIT in SQL, so this fetches
      // exactly the latest N instead of the whole index.
      //
      // The strip is decorative — `ArticlesSection` renders nothing for an
      // empty list — so a failure here degrades it instead of 500-ing the
      // homepage. Two Postgres wire-protocol errors on this exact query
      // (Sentry DCM-T0 / DCM-T1) took the whole page down for what is a
      // supporting section; the connection-layer cause is separate and still
      // reported to Sentry by `sentryFunctionMiddleware`.
      optionalLoaderData(
        getArticlesIndexFn({ data: { limit: LATEST_ARTICLES_COUNT } }),
        [],
        "home:latest-articles",
      ),
    ]);

    return { categories, articles };
  },
  head: () => {
    const metaConfig = createBrandMeta({
      title: "Dream Coach Match | The Global Platform for Coaching",
      description: BRAND.description,
      ogTitle: "Dream Coach Match | The Global Platform for Coaching",
      ogDescription: BRAND.description,
      ogType: "website",
      ogUrl: "https://dreamcoachmatch.com",
      twitterCard: "summary_large_image",
      twitterTitle: "Dream Coach Match | The Global Platform for Coaching",
      twitterDescription: BRAND.description,
      canonical: "https://dreamcoachmatch.com",
      keywords: [
        "life coach",
        "business coach",
        "health coach",
        "executive coach",
        "coaching marketplace",
        "find a coach",
        "personal development",
        "professional coaching",
        "certified coaches",
      ],
      author: "Dream Coach Match",
    });

    const serviceStructuredData = {
      "@context": "https://schema.org",
      "@type": "Service",
      name: "Coaching Discovery & Matching",
      description: BRAND.description,
      provider: { "@id": BRAND_ORGANIZATION_ID },
      serviceType: [
        "Life Coaching",
        "Business Coaching",
        "Health Coaching",
        "Executive Coaching",
        "Career Coaching",
      ],
      areaServed: "Worldwide",
      hasOfferCatalog: {
        "@type": "OfferCatalog",
        name: "Coaching Services",
        itemListElement: [
          {
            "@type": "Offer",
            itemOffered: {
              "@type": "Service",
              name: "Life Coaching",
              description:
                "Personal development and life transformation coaching",
            },
          },
          {
            "@type": "Offer",
            itemOffered: {
              "@type": "Service",
              name: "Business Coaching",
              description:
                "Business growth and leadership development coaching",
            },
          },
          {
            "@type": "Offer",
            itemOffered: {
              "@type": "Service",
              name: "Health Coaching",
              description: "Wellness and health transformation coaching",
            },
          },
        ],
      },
    };

    // Organization JSON-LD is injected site-wide from __root.tsx. The home
    // page layers on the richer set: WebSite (sitelinks search box),
    // BreadcrumbList, the Service block, and the brand Person nodes (which
    // resolve the Organization's `founder` reference).
    return {
      ...metaConfig,
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(createBrandWebsiteStructuredData()),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(serviceStructuredData),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            createBreadcrumbStructuredData([{ name: "Home", url: BRAND_URL }]),
          ),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            createBrandPersonStructuredData(BRAND_PEOPLE.yuri),
          ),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            createBrandPersonStructuredData(BRAND_PEOPLE.marcel),
          ),
        },
      ],
    };
  },
  component: HomePage,
});
